import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    CircularProgress,
    LinearProgress,
    Alert,
    IconButton,
    Chip,
    Fade,
    alpha,
} from '@mui/material';
import {
    Close as CloseIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Schedule as ScheduleIcon,
    PeopleAlt as PeopleAltIcon,
    SportsBaseball as BaseballIcon,
} from '@mui/icons-material';
import {
    createProfile,
    scrapeProfile,
    scrapeAllProfiles,
    scrapeRoster,
    importPgCommitments,
    importCollegeRoster,
    getPgColleges,
    setGroupLogo,
    PgCollege,
    deleteProfile,
    unlinkProfile,
    matchProfiles,
    BaseballRecruitProfile,
    MatchProfilesResponse,
    CollegeRosterMatch,
    CollegeRosterUnmatched,
    scrapeGroupMembers,
    searchBrSummerStats,
    getScrapeJobStatus,
    scrapeCollegeStats,
    refreshCollegeStats,
    UnmatchedCollegePlayer,
    scrapeSummerStats,
    UnmatchedSummerPlayer,
    confirmCollegeRoster,
    ConfirmCollegeRosterPlayer,
} from 'api/BaseballRecruits';
import BaseballRecruitMatchReview from 'modules/community/components/baseball/BaseballRecruitMatchReview';
import CollegeRosterReview from 'modules/community/components/baseball/CollegeRosterReview';
import CollegeStatsMatchReview from 'modules/community/components/baseball/CollegeStatsMatchReview';
import SummerStatsMatchReview from 'modules/community/components/baseball/SummerStatsMatchReview';
import GetPlayerStatsSection from 'modules/community/components/baseball/GetPlayerStatsSection';
import ImportRostersSection from 'modules/community/components/baseball/ImportRostersSection';
import ImportStatsSection from 'modules/community/components/baseball/ImportStatsSection';
import { ProfilePhotosSection } from 'modules/community/components/baseball/ProfilePhotosSection';
import { PreviousScrapesSection } from 'modules/community/components/baseball/PreviousScrapesSection';
import { usePusher } from 'contexts/PusherContext';
import { useSelector } from 'react-redux';
import useTheme from '@mui/system/useTheme';
import { userSelector } from 'redux/reducers/user';
import { Group } from 'utils';

const SCRAPE_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes — matches backend job timeout + buffer

interface BaseballRecruitScraperDialogProps {
    open: boolean;
    onClose: () => void;
    group: Group | undefined;
    profiles: BaseballRecruitProfile[];
    onSuccess?: () => void;
    onScrapingComplete?: () => void;
}

const BaseballRecruitScraperDialog: React.FC<BaseballRecruitScraperDialogProps> = ({
    open,
    onClose,
    group,
    profiles,
    onSuccess,
    onScrapingComplete,
}) => {
    const pusher = usePusher();
    const { userData } = useSelector(userSelector);
    const theme = useTheme();

    const [newUrl, setNewUrl] = useState('');
    const [adding, setAdding] = useState(false);
    const [scrapingAll, setScrapingAll] = useState(false);
    const [rosterUrl, setRosterUrl] = useState('');
    const [importingRoster, setImportingRoster] = useState(false);
    const [pgColleges, setPgColleges] = useState<PgCollege[]>([]);
    const [pgCollegesLoading, setPgCollegesLoading] = useState(false);
    const [pgCollege, setPgCollege] = useState<PgCollege | null>(null);
    const [pgYear, setPgYear] = useState(2026);
    const [importingPg, setImportingPg] = useState(false);
    const [pgUnmatched, setPgUnmatched] = useState<
        { name: string; position: string; high_school: string; state: string }[]
    >([]);
    const [collegeLogoUrl, setCollegeLogoUrl] = useState('');
    const [settingLogo, setSettingLogo] = useState(false);
    const [scrapingProfiles, setScrapingProfiles] = useState<Set<string>>(new Set());
    const [scrapingTotal, setScrapingTotal] = useState(0);
    const [completedPlayers, setCompletedPlayers] = useState<string[]>([]);
    const scrapeSnapshotRef = useRef<Record<string, string | null>>({});
    const scrapingStartTimesRef = useRef<Record<string, number>>({});
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const onSuccessRef = useRef(onSuccess);
    onSuccessRef.current = onSuccess;

    const [matchReviewOpen, setMatchReviewOpen] = useState(false);
    const [matchResults, setMatchResults] = useState<MatchProfilesResponse | null>(null);
    const [loadingMatches, setLoadingMatches] = useState(false);
    const prevScrapingCountRef = useRef(0);

    const [collegeRosterUrl, setCollegeRosterUrl] = useState('');
    const [importingCollegeRoster, setImportingCollegeRoster] = useState(false);
    const [collegeRosterMatched, setCollegeRosterMatched] = useState<CollegeRosterMatch[]>([]);
    const [collegeRosterUnmatched, setCollegeRosterUnmatched] = useState<CollegeRosterUnmatched[]>(
        [],
    );
    const [collegeRosterReviewOpen, setCollegeRosterReviewOpen] = useState(false);
    const [refreshingPhotos, setRefreshingPhotos] = useState(false);
    const [photoRefreshUrl, setPhotoRefreshUrl] = useState('');
    const [searchingMembers, setSearchingMembers] = useState(false);

    const [collegeStatsUrl, setCollegeStatsUrl] = useState('');
    const [importingCollegeStats, setImportingCollegeStats] = useState(false);
    const [refreshingCollegeStats, setRefreshingCollegeStats] = useState(false);
    const [collegeStatsUnmatched, setCollegeStatsUnmatched] = useState<UnmatchedCollegePlayer[]>(
        [],
    );
    const [collegeStatsMatchOpen, setCollegeStatsMatchOpen] = useState(false);
    const [collegeStatsMatchUrl, setCollegeStatsMatchUrl] = useState('');

    const [summerStatsUrl, setSummerStatsUrl] = useState('');
    const [importingSummerStats, setImportingSummerStats] = useState(false);
    const [summerStatsUnmatched, setSummerStatsUnmatched] = useState<UnmatchedSummerPlayer[]>([]);
    const [summerStatsMatchOpen, setSummerStatsMatchOpen] = useState(false);
    const [summerStatsMatchUrl, setSummerStatsMatchUrl] = useState('');
    const [searchingBr, setSearchingBr] = useState(false);
    const [brSearchStartTime, setBrSearchStartTime] = useState<number | null>(null);
    const [brElapsedSeconds, setBrElapsedSeconds] = useState(0);

    const profilesWithCollegeStats = profiles.filter((p) => p.college_stats_url);
    const latestCollegeScrape = profilesWithCollegeStats
        .map((p) => p.college_last_scraped_at)
        .filter(Boolean)
        .sort()
        .pop();

    const detectedRosterUrl = useMemo(() => {
        const statsUrl = profiles.find((p) => p.college_stats_url)?.college_stats_url;
        if (!statsUrl) return '';
        const match = statsUrl.match(/^(https?:\/\/[^/]+\/sports\/[^/]+\/)stats/i);
        return match ? `${match[1]}roster` : '';
    }, [profiles]);

    const unlinkedProfileCount = profiles.filter(
        (p) => (p.scraped_data || p.player_bio) && !p.individual_uuid,
    ).length;

    const SCRAPING_MESSAGES = [
        'Scanning batting averages...',
        'Checking pitching records...',
        'Analyzing fielding stats...',
        'Pulling game logs...',
        'Crunching the numbers...',
        'Reviewing season performance...',
        'Collecting career totals...',
        'Looking up player bios...',
        'Fetching RBI counts...',
        'Tallying strikeouts...',
        'Measuring ERA trends...',
        'Compiling stolen base records...',
    ];
    const ROSTER_MESSAGES = [
        'Connecting to MaxPreps...',
        'Scanning team roster page...',
        'Identifying player profiles...',
        'Extracting player URLs...',
        'Building player list...',
        'Almost there...',
    ];
    const PG_MESSAGES = [
        'Connecting to Perfect Game...',
        'Scanning commitment list...',
        'Searching MaxPreps for players...',
        'Matching by name and school...',
        'Cross-referencing state data...',
        'Building player profiles...',
        'Almost there...',
    ];

    const COLLEGE_ROSTER_MESSAGES = [
        'Connecting to college athletics site...',
        'Scanning roster page...',
        'Extracting player data...',
        'Downloading profile photos...',
        'Matching players to existing members...',
        'Almost there...',
    ];

    const PHOTO_REFRESH_MESSAGES = [
        'Connecting to college athletics site...',
        'Scanning roster for photos...',
        'Matching players to existing profiles...',
        'Downloading updated profile images...',
        'Saving photos to storage...',
        'Almost done...',
    ];

    const [statusMessageIdx, setStatusMessageIdx] = useState(0);
    const MEMBER_SEARCH_MESSAGES = [
        'Searching MaxPreps for group members...',
        'Matching player names...',
        'Finding baseball profiles...',
        'Checking for existing records...',
        'Almost done searching...',
    ];
    const COLLEGE_STATS_MESSAGES = [
        'Connecting to college athletics site...',
        'Extracting team data from WMT...',
        'Fetching player season stats...',
        'Loading career totals...',
        'Pulling game-by-game data...',
        'Matching players to profiles...',
        'Almost there...',
    ];
    const BR_SEARCH_MESSAGES = [
        'Searching Baseball Reference for players...',
        'Matching player names...',
        'Scraping summer league stats...',
        'Filtering summer league rows...',
        'Building season data...',
        'Computing career totals...',
        'Matching players to profiles...',
        'Almost done...',
    ];

    const isActive =
        importingRoster ||
        importingPg ||
        importingCollegeRoster ||
        importingCollegeStats ||
        refreshingCollegeStats ||
        refreshingPhotos ||
        searchingMembers ||
        searchingBr ||
        scrapingProfiles.size > 0;
    const activeMessages = searchingBr
        ? BR_SEARCH_MESSAGES
        : searchingMembers
        ? MEMBER_SEARCH_MESSAGES
        : refreshingPhotos
        ? PHOTO_REFRESH_MESSAGES
        : importingCollegeStats || refreshingCollegeStats
        ? COLLEGE_STATS_MESSAGES
        : importingCollegeRoster
        ? COLLEGE_ROSTER_MESSAGES
        : importingPg
        ? PG_MESSAGES
        : importingRoster
        ? ROSTER_MESSAGES
        : SCRAPING_MESSAGES;

    const handlePgAccordionExpand = useCallback(
        (_: React.SyntheticEvent, expanded: boolean) => {
            if (expanded && pgColleges.length === 0 && !pgCollegesLoading) {
                setPgCollegesLoading(true);
                getPgColleges()
                    .then((res) => setPgColleges(res.data.colleges ?? []))
                    .catch(() => {})
                    .finally(() => setPgCollegesLoading(false));
            }
        },
        [pgColleges.length, pgCollegesLoading],
    );

    useEffect(() => {
        if (!isActive) return;
        setStatusMessageIdx(0);
        const interval = setInterval(() => {
            setStatusMessageIdx((prev) => (prev + 1) % activeMessages.length);
        }, 3000);
        return () => clearInterval(interval);
    }, [isActive, activeMessages.length]);

    const scrapeProgress =
        scrapingTotal > 0 ? ((scrapingTotal - scrapingProfiles.size) / scrapingTotal) * 100 : 0;

    const [scrapeStartTime, setScrapeStartTime] = useState<number | null>(null);

    useEffect(() => {
        if (scrapingProfiles.size > 0 && !scrapeStartTime) {
            setScrapeStartTime(Date.now());
        } else if (scrapingProfiles.size === 0) {
            setScrapeStartTime(null);
        }
    }, [scrapingProfiles.size, scrapeStartTime]);

    const [elapsedSeconds, setElapsedSeconds] = useState(0);

    useEffect(() => {
        if (!scrapeStartTime) {
            setElapsedSeconds(0);
            return;
        }
        const tick = setInterval(() => {
            setElapsedSeconds(Math.floor((Date.now() - scrapeStartTime) / 1000));
        }, 1000);
        return () => clearInterval(tick);
    }, [scrapeStartTime]);

    const formatElapsed = (secs: number) => {
        const m = Math.floor(secs / 60);
        const s = secs % 60;
        return `${m}:${s.toString().padStart(2, '0')}`;
    };

    const getEta = () => {
        if (!scrapeStartTime || scrapingTotal === 0) return '';
        const completed = scrapingTotal - scrapingProfiles.size;
        if (completed < 1) return 'Estimating...';
        const elapsed = (Date.now() - scrapeStartTime) / 1000;
        const perPlayer = elapsed / completed;
        const remaining = Math.round(perPlayer * scrapingProfiles.size);
        if (remaining < 5) return 'Almost done...';
        if (remaining < 60) return `~${remaining}s remaining`;
        return `~${Math.ceil(remaining / 60)} min remaining`;
    };

    useEffect(() => {
        if (!brSearchStartTime) {
            setBrElapsedSeconds(0);
            return;
        }
        const tick = setInterval(() => {
            setBrElapsedSeconds(Math.floor((Date.now() - brSearchStartTime) / 1000));
        }, 1000);
        return () => clearInterval(tick);
    }, [brSearchStartTime]);

    // Pusher: listen for scrape completions — only update counters, no refresh
    useEffect(() => {
        if (!pusher || !userData?.uuid || !open) return;

        const channel = pusher.subscribe(`private-users.${userData.uuid}`);

        const handleComplete = (data: any) => {
            setScrapingProfiles((prev) => {
                const next = new Set(prev);
                next.delete(data.profile_uuid);
                return next;
            });
            if (data.results?.player_name) {
                setCompletedPlayers((prev) => [...prev, data.results.player_name]);
            }
        };

        const handleFailed = (data: any) => {
            setScrapingProfiles((prev) => {
                const next = new Set(prev);
                next.delete(data.profile_uuid);
                return next;
            });
            setErrorMessage(data.error || 'Scraping failed');
        };

        channel.bind('baseball-recruit.scraping.complete', handleComplete);
        channel.bind('baseball-recruit.scraping.failed', handleFailed);

        return () => {
            channel.unbind('baseball-recruit.scraping.complete', handleComplete);
            channel.unbind('baseball-recruit.scraping.failed', handleFailed);
        };
    }, [pusher, userData?.uuid, open]);

    // Single refresh when all scraping finishes (not during)
    const prevScrapingSizeRef = useRef(0);
    useEffect(() => {
        const wasActive = prevScrapingSizeRef.current > 0;
        prevScrapingSizeRef.current = scrapingProfiles.size;
        if (wasActive && scrapingProfiles.size === 0) {
            onSuccessRef.current?.();
        }
    }, [scrapingProfiles.size]);

    // Fallback: poll to refresh profiles while scraping is active, in case Pusher events are missed
    const staleIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    useEffect(() => {
        if (staleIntervalRef.current) clearInterval(staleIntervalRef.current);
        if (scrapingProfiles.size > 0) {
            staleIntervalRef.current = setInterval(() => {
                onSuccessRef.current?.();
            }, 10000);
        }
        return () => {
            if (staleIntervalRef.current) clearInterval(staleIntervalRef.current);
        };
    }, [scrapingProfiles.size]);

    // Reconcile scrapingProfiles with actual profile data after polling refreshes.
    // Also clears profiles that have been scraping longer than SCRAPE_TIMEOUT_MS
    // as a safety net for silent backend failures (e.g. queue worker timeout/SIGKILL).
    useEffect(() => {
        if (scrapingProfiles.size === 0) return;
        const snapshot = scrapeSnapshotRef.current;
        const startTimes = scrapingStartTimesRef.current;
        const now = Date.now();
        const completed: string[] = [];
        const timedOut: string[] = [];
        scrapingProfiles.forEach((uuid) => {
            const profile = profiles.find((p) => p.uuid === uuid);
            if (!profile) return;
            const snapshotVal = snapshot[uuid];
            if (
                profile.scraped_data ||
                (profile.last_scraped_at && profile.last_scraped_at !== snapshotVal)
            ) {
                completed.push(uuid);
                return;
            }
            const startedAt = startTimes[uuid];
            if (startedAt && now - startedAt > SCRAPE_TIMEOUT_MS) {
                timedOut.push(uuid);
            }
        });
        const allDone = [...completed, ...timedOut];
        if (allDone.length > 0) {
            setScrapingProfiles((prev) => {
                const next = new Set(prev);
                allDone.forEach((uuid) => next.delete(uuid));
                return next;
            });
            const newNames = completed
                .map((uuid) => {
                    const p = profiles.find((pr) => pr.uuid === uuid);
                    const bio = p?.player_bio;
                    return bio ? `${bio.first_name ?? ''} ${bio.last_name ?? ''}`.trim() : '';
                })
                .filter(Boolean);
            if (newNames.length > 0) {
                setCompletedPlayers((prev) => [...prev, ...newNames]);
            }
            if (timedOut.length > 0) {
                setErrorMessage(
                    `${timedOut.length} scrape job(s) did not complete in time. They may still be processing — refresh to check.`,
                );
            }
        }
    }, [profiles, scrapingProfiles]);

    const handleAddUrl = useCallback(async () => {
        const raw = newUrl.trim();
        if (!raw || !group?.uuid) return;

        const url = normalizeMaxprepsUrl(raw);
        setAdding(true);
        setErrorMessage('');
        try {
            await createProfile({
                group_uuid: group.uuid,
                maxpreps_url: url,
            });
            setNewUrl('');
            setSuccessMessage('Player URL added');
            onSuccess?.();
        } catch (e: any) {
            setErrorMessage(e?.response?.data?.message || 'Failed to add URL');
        }
        setAdding(false);
    }, [newUrl, group?.uuid, onSuccess]);

    const handleDeleteProfile = useCallback(
        async (profileUuid: string) => {
            setErrorMessage('');
            try {
                await deleteProfile(profileUuid);
                setSuccessMessage('Profile removed');
                onSuccess?.();
            } catch (e: any) {
                setErrorMessage(e?.response?.data?.message || 'Failed to remove profile');
            }
        },
        [onSuccess],
    );

    const handleUnlinkProfile = useCallback(
        async (profileUuid: string) => {
            setErrorMessage('');
            try {
                await unlinkProfile(profileUuid);
                setSuccessMessage('Profile unlinked from individual');
                onSuccess?.();
            } catch (e: any) {
                setErrorMessage(e?.response?.data?.message || 'Failed to unlink profile');
            }
        },
        [onSuccess],
    );

    const handleScrape = useCallback(
        async (profileUuid: string) => {
            setErrorMessage('');
            const profile = profiles.find((p) => p.uuid === profileUuid);
            scrapeSnapshotRef.current = {
                ...scrapeSnapshotRef.current,
                [profileUuid]: profile?.last_scraped_at ?? null,
            };
            scrapingStartTimesRef.current = {
                ...scrapingStartTimesRef.current,
                [profileUuid]: Date.now(),
            };
            setScrapingProfiles((prev) => new Set(prev).add(profileUuid));
            setScrapingTotal((prev) => (prev === 0 ? 1 : prev));
            try {
                await scrapeProfile(profileUuid);
                onSuccess?.();
            } catch (e: any) {
                setScrapingProfiles((prev) => {
                    const next = new Set(prev);
                    next.delete(profileUuid);
                    return next;
                });
                setErrorMessage(e?.response?.data?.message || 'Failed to start scrape');
            }
        },
        [onSuccess, profiles],
    );

    const handleScrapeAll = useCallback(async () => {
        if (!group?.uuid) return;
        setScrapingAll(true);
        setErrorMessage('');
        setCompletedPlayers([]);
        try {
            await scrapeAllProfiles(group.uuid);
            const scrapable = profiles.filter((p) => p.maxpreps_url);
            const snapshot: Record<string, string | null> = {};
            const startTimes: Record<string, number> = {};
            const now = Date.now();
            scrapable.forEach((p) => {
                snapshot[p.uuid] = p.last_scraped_at;
                startTimes[p.uuid] = now;
            });
            scrapeSnapshotRef.current = snapshot;
            scrapingStartTimesRef.current = startTimes;
            setScrapingTotal(scrapable.length);
            setScrapingProfiles(new Set(scrapable.map((p) => p.uuid)));
            onSuccess?.();
            setTimeout(() => onSuccessRef.current?.(), 3000);
        } catch (e: any) {
            setErrorMessage(e?.response?.data?.message || 'Failed to start scrape');
        }
        setScrapingAll(false);
    }, [group?.uuid, profiles, onSuccess]);

    const handleImportRoster = useCallback(async () => {
        const raw = rosterUrl.trim();
        if (!raw || !group?.uuid) return;

        setImportingRoster(true);
        setErrorMessage('');
        setCompletedPlayers([]);
        try {
            const response = await scrapeRoster(group.uuid, raw);
            const {
                players_found,
                profiles_created,
                scrape_jobs_dispatched,
                dispatched_profile_uuids,
            } = response.data;
            setSuccessMessage(
                `Found ${players_found} player(s). ${profiles_created} new profile(s) created. ${scrape_jobs_dispatched} scrape job(s) started.`,
            );
            setRosterUrl('');

            await onSuccessRef.current?.();

            if (
                scrape_jobs_dispatched > 0 &&
                dispatched_profile_uuids &&
                dispatched_profile_uuids.length > 0
            ) {
                const uuids = dispatched_profile_uuids;
                const snapshot: Record<string, string | null> = {};
                const startTimes: Record<string, number> = {};
                const now = Date.now();
                uuids.forEach((uuid) => {
                    snapshot[uuid] = null;
                    startTimes[uuid] = now;
                });
                scrapeSnapshotRef.current = snapshot;
                scrapingStartTimesRef.current = startTimes;
                setScrapingProfiles(new Set(uuids));
                setScrapingTotal(uuids.length);
            } else {
                const scrapable = profiles.filter((p) => p.maxpreps_url);
                const snapshot: Record<string, string | null> = {};
                const startTimes: Record<string, number> = {};
                const now = Date.now();
                scrapable.forEach((p) => {
                    snapshot[p.uuid] = p.last_scraped_at;
                    startTimes[p.uuid] = now;
                });
                scrapeSnapshotRef.current = snapshot;
                scrapingStartTimesRef.current = startTimes;
                setScrapingTotal(scrapable.length);
                setScrapingProfiles(new Set(scrapable.map((p) => p.uuid)));
            }

            if (players_found > 0 && group?.uuid) {
                setTimeout(async () => {
                    setLoadingMatches(true);
                    try {
                        const matchResponse = await matchProfiles(group.uuid);
                        setMatchResults(matchResponse.data);
                        setMatchReviewOpen(true);
                    } catch {
                        // Match review will be available via the button
                    } finally {
                        setLoadingMatches(false);
                    }
                }, 1500);
            }
        } catch (e: any) {
            setErrorMessage(
                e?.response?.data?.error || e?.response?.data?.message || 'Failed to import roster',
            );
        }
        setImportingRoster(false);
    }, [rosterUrl, group?.uuid, profiles]);

    const handleImportPgCommitments = useCallback(async () => {
        if (!pgCollege || !group?.uuid) return;

        setImportingPg(true);
        setErrorMessage('');
        setPgUnmatched([]);
        setCompletedPlayers([]);
        try {
            const response = await importPgCommitments(group.uuid, pgCollege.name, pgYear);
            const {
                players_found,
                matched_count,
                profiles_created,
                scrape_jobs_dispatched,
                unmatched_players,
                college_logo_url,
            } = response.data;
            if (college_logo_url) {
                setCollegeLogoUrl(college_logo_url);
            }
            setSuccessMessage(
                `Found ${players_found} player(s). ${
                    matched_count ?? profiles_created
                } matched to MaxPreps. ${profiles_created} new profile(s) created. ${scrape_jobs_dispatched} scrape job(s) started.`,
            );
            if (unmatched_players?.length) {
                setPgUnmatched(unmatched_players);
            }
            setPgCollege(null);
            onSuccess?.();

            if (scrape_jobs_dispatched > 0) {
                // Only mark profiles without existing scraped_data as actively scraping,
                // to avoid falsely flagging already-scraped profiles as in-progress when
                // the PG import API doesn't return dispatched_profile_uuids.
                const scrapable = profiles.filter((p) => p.maxpreps_url && !p.scraped_data);
                const snapshot: Record<string, string | null> = {};
                const startTimes: Record<string, number> = {};
                const now = Date.now();
                scrapable.forEach((p) => {
                    snapshot[p.uuid] = p.last_scraped_at;
                    startTimes[p.uuid] = now;
                });
                scrapeSnapshotRef.current = snapshot;
                scrapingStartTimesRef.current = startTimes;
                setScrapingTotal(scrapable.length);
                setScrapingProfiles(new Set(scrapable.map((p) => p.uuid)));
            }
        } catch (e: any) {
            setErrorMessage(
                e?.response?.data?.error ||
                    e?.response?.data?.message ||
                    'Failed to import from Perfect Game',
            );
        }
        setImportingPg(false);
    }, [pgCollege, pgYear, group?.uuid, profiles, onSuccess]);

    const handleSetGroupLogo = useCallback(async () => {
        if (!collegeLogoUrl || !group?.uuid) return;
        setSettingLogo(true);
        try {
            await setGroupLogo(group.uuid, collegeLogoUrl);
            setSuccessMessage('Group logo updated.');
            setCollegeLogoUrl('');
            onSuccess?.();
        } catch {
            setErrorMessage('Failed to set group logo.');
        }
        setSettingLogo(false);
    }, [collegeLogoUrl, group?.uuid, onSuccess]);

    const handleImportCollegeRoster = useCallback(async () => {
        const raw = collegeRosterUrl.trim();
        if (!raw || !group?.uuid) return;

        setImportingCollegeRoster(true);
        setErrorMessage('');
        try {
            const response = await importCollegeRoster(group.uuid, raw);
            const { matched, unmatched, players_found } = response.data;
            setCollegeRosterMatched(matched);
            setCollegeRosterUnmatched(unmatched);
            setCollegeRosterReviewOpen(true);
            setSuccessMessage(
                `Found ${players_found} player(s) on roster. ${matched.length} matched to existing members.`,
            );
            setCollegeRosterUrl('');
        } catch (e: any) {
            setErrorMessage(
                e?.response?.data?.error ||
                    e?.response?.data?.message ||
                    'Failed to import college roster',
            );
        }
        setImportingCollegeRoster(false);
    }, [collegeRosterUrl, group?.uuid]);

    const handleRefreshCollegePhotos = useCallback(async () => {
        const url = photoRefreshUrl.trim() || detectedRosterUrl;
        if (!url || !group?.uuid) return;

        setRefreshingPhotos(true);
        setErrorMessage('');
        setSuccessMessage('');
        try {
            const response = await importCollegeRoster(group.uuid, url);
            const { matched } = response.data;

            if (matched.length === 0) {
                setSuccessMessage('No roster players matched existing members.');
                setRefreshingPhotos(false);
                return;
            }

            const playersToConfirm: ConfirmCollegeRosterPlayer[] = matched.map((m) => ({
                first_name: m.player.first_name,
                last_name: m.player.last_name,
                action: 'link' as const,
                individual_uuid: m.match.individual_uuid ?? undefined,
                photo_url: m.player.photo_url,
                position: m.player.position,
                jersey: m.player.jersey,
                hometown: m.player.hometown,
                high_school: m.player.high_school,
                home_state: m.player.state,
            }));

            const confirmRes = await confirmCollegeRoster(group.uuid, playersToConfirm);
            setSuccessMessage(`Updated photos for ${confirmRes.data.linked} player(s).`);
            onSuccessRef.current?.();
        } catch (e: any) {
            setErrorMessage(
                e?.response?.data?.error ||
                    e?.response?.data?.message ||
                    'Failed to refresh college photos',
            );
        }
        setRefreshingPhotos(false);
    }, [photoRefreshUrl, detectedRosterUrl, group?.uuid]);

    const handleImportCollegeStats = useCallback(async () => {
        let raw = collegeStatsUrl.trim();
        if (!raw || !group?.uuid) return;
        if (!/^https?:\/\//i.test(raw)) raw = `https://${raw}`;
        if (!/^https?:\/\/[a-z0-9.-]+\.[a-z]{2,}/i.test(raw)) {
            setErrorMessage(
                'Please enter a valid stats page URL (e.g. https://thesundevils.com/sports/baseball/stats)',
            );
            return;
        }

        setImportingCollegeStats(true);
        setErrorMessage('');
        setCollegeStatsUnmatched([]);
        try {
            const response = await scrapeCollegeStats(group.uuid, raw);
            const { unmatched_players } = response.data;
            setCollegeStatsUrl('');
            onSuccess?.();
            if (unmatched_players?.length) {
                setCollegeStatsUnmatched(unmatched_players);
                setCollegeStatsMatchUrl(raw);
                setCollegeStatsMatchOpen(true);
            } else {
                onScrapingComplete?.();
            }
        } catch (e: any) {
            setErrorMessage(
                e?.response?.data?.error ||
                    e?.response?.data?.message ||
                    'Failed to import college stats',
            );
        }
        setImportingCollegeStats(false);
    }, [collegeStatsUrl, group?.uuid, onSuccess, onScrapingComplete]);

    const handleRefreshCollegeStats = useCallback(async () => {
        if (!group?.uuid) return;
        setRefreshingCollegeStats(true);
        setErrorMessage('');
        setCollegeStatsUnmatched([]);
        try {
            const dispatchResponse = await refreshCollegeStats(group.uuid);
            const jobId = dispatchResponse.data.job_id;

            const pollForResults = async (): Promise<any> => {
                for (let i = 0; i < 120; i++) {
                    await new Promise((r) => setTimeout(r, 3000));
                    const statusResponse = await getScrapeJobStatus(jobId);
                    const { status, results, error } = statusResponse.data;
                    if (status === 'complete') return results;
                    if (status === 'failed')
                        throw new Error(error || 'College stats refresh failed');
                }
                throw new Error('College stats refresh timed out');
            };

            const results = await pollForResults();
            onSuccess?.();
            if (results?.unmatched_players?.length) {
                setCollegeStatsUnmatched(results.unmatched_players);
                setCollegeStatsMatchUrl(results.stats_url || '');
                setCollegeStatsMatchOpen(true);
            } else {
                setSuccessMessage(results?.message || 'College stats refreshed.');
                onScrapingComplete?.();
            }
        } catch (e: any) {
            setErrorMessage(
                e?.response?.data?.error ||
                    e?.response?.data?.message ||
                    e?.message ||
                    'Failed to refresh college stats',
            );
        }
        setRefreshingCollegeStats(false);
    }, [group?.uuid, onSuccess, onScrapingComplete]);

    const handleImportSummerStats = useCallback(async () => {
        let raw = summerStatsUrl.trim();
        if (!raw || !group?.uuid) return;
        if (!/^https?:\/\//i.test(raw)) raw = `https://${raw}`;
        if (!/baseball-reference\.com\/register\/player\.fcgi/i.test(raw)) {
            setErrorMessage(
                'Please enter a Baseball Reference register player URL (e.g. https://www.baseball-reference.com/register/player.fcgi?id=hairst000lan)',
            );
            return;
        }

        setImportingSummerStats(true);
        setErrorMessage('');
        setSummerStatsUnmatched([]);
        try {
            const response = await scrapeSummerStats(group.uuid, raw);
            const { unmatched_players, message } = response.data;
            setSummerStatsUrl('');
            setSuccessMessage(message);
            onSuccess?.();
            if (unmatched_players?.length) {
                setSummerStatsUnmatched(unmatched_players);
                setSummerStatsMatchUrl(raw);
                setSummerStatsMatchOpen(true);
            } else {
                onScrapingComplete?.();
            }
        } catch (e: any) {
            setErrorMessage(
                e?.response?.data?.error ||
                    e?.response?.data?.message ||
                    'Failed to import summer stats',
            );
        }
        setImportingSummerStats(false);
    }, [summerStatsUrl, group?.uuid, onSuccess, onScrapingComplete]);

    const handleSearchMembers = useCallback(async () => {
        if (!group?.uuid) return;
        setSearchingMembers(true);
        setErrorMessage('');
        setSuccessMessage('');
        setCompletedPlayers([]);
        try {
            const response = await scrapeGroupMembers(group.uuid);
            const {
                searched,
                matched,
                profiles_created,
                scrape_jobs_dispatched,
                dispatched_profile_uuids,
            } = response.data;
            setSuccessMessage(
                `Searched ${searched} member(s). ${matched} found on MaxPreps. ${profiles_created} profile(s) created. ${scrape_jobs_dispatched} scrape job(s) started.`,
            );
            if (scrape_jobs_dispatched > 0 && dispatched_profile_uuids?.length) {
                const uuids = dispatched_profile_uuids as string[];
                const snapshot: Record<string, string | null> = {};
                uuids.forEach((uuid) => {
                    snapshot[uuid] = 'pending';
                });
                scrapeSnapshotRef.current = snapshot;
                setScrapingProfiles(new Set(uuids));
                setScrapingTotal(uuids.length);
            }
            onSuccessRef.current?.();
            if (scrape_jobs_dispatched > 0) {
                setTimeout(() => onSuccessRef.current?.(), 3000);
            }
        } catch (e: any) {
            setErrorMessage(
                e?.response?.data?.error ||
                    e?.response?.data?.message ||
                    'Failed to search MaxPreps for members',
            );
        }
        setSearchingMembers(false);
    }, [group?.uuid]);

    const handleSearchBr = useCallback(async () => {
        if (!group?.uuid) return;
        setSearchingBr(true);
        setBrSearchStartTime(Date.now());
        setErrorMessage('');
        setSuccessMessage('');
        setSummerStatsUnmatched([]);
        try {
            const dispatchResponse = await searchBrSummerStats(group.uuid);
            const jobId = dispatchResponse.data.job_id;

            const pollForResults = async (): Promise<any> => {
                for (let i = 0; i < 120; i++) {
                    await new Promise((r) => setTimeout(r, 3000));
                    const statusResponse = await getScrapeJobStatus(jobId);
                    const { status, results, error } = statusResponse.data;
                    if (status === 'complete') return results;
                    if (status === 'failed')
                        throw new Error(error || 'Baseball Reference search failed');
                }
                throw new Error('Baseball Reference search timed out');
            };

            const results = await pollForResults();
            setSuccessMessage(results?.message || 'Search complete.');
            onSuccessRef.current?.();
            if (results?.unmatched_players?.length) {
                setSummerStatsUnmatched(results.unmatched_players);
                setSummerStatsMatchUrl('');
                setSummerStatsMatchOpen(true);
            } else {
                onScrapingComplete?.();
            }
        } catch (e: any) {
            setErrorMessage(
                e?.response?.data?.error ||
                    e?.response?.data?.message ||
                    e?.message ||
                    'Failed to search Baseball Reference',
            );
        }
        setSearchingBr(false);
        setBrSearchStartTime(null);
    }, [group?.uuid, onScrapingComplete]);

    const handleReviewMatches = useCallback(async () => {
        if (!group?.uuid) return;
        setLoadingMatches(true);
        setErrorMessage('');
        try {
            const response = await matchProfiles(group.uuid);
            setMatchResults(response.data);
            setMatchReviewOpen(true);
        } catch (e: any) {
            setErrorMessage(e?.response?.data?.message || 'Failed to load match data');
        } finally {
            setLoadingMatches(false);
        }
    }, [group?.uuid]);

    // Reconcile tracking set with actual profile state (handles sync queue race)
    useEffect(() => {
        if (scrapingProfiles.size === 0) return;
        const completed = new Set<string>();
        scrapingProfiles.forEach((uuid) => {
            const profile = profiles.find((p) => p.uuid === uuid);
            if (profile && (profile.scraped_data || profile.scrape_status === 'failed')) {
                completed.add(uuid);
            }
        });
        if (completed.size > 0) {
            setScrapingProfiles((prev) => {
                const next = new Set(prev);
                completed.forEach((uuid) => next.delete(uuid));
                return next;
            });
            completed.forEach((uuid) => {
                const profile = profiles.find((p) => p.uuid === uuid);
                const name = profile?.player_bio
                    ? `${profile.player_bio.first_name} ${profile.player_bio.last_name}`
                    : '';
                if (name) {
                    setCompletedPlayers((prev) => [...prev, name]);
                }
            });
        }
    }, [profiles, scrapingProfiles]);

    // Auto-open match review when scraping finishes and there are unlinked profiles,
    // or go straight to card view if all profiles are already linked
    useEffect(() => {
        const wasScrapingBefore = prevScrapingCountRef.current > 0;
        const scrapingJustFinished = wasScrapingBefore && scrapingProfiles.size === 0;
        prevScrapingCountRef.current = scrapingProfiles.size;

        if (scrapingJustFinished) {
            if (unlinkedProfileCount > 0 && !matchReviewOpen) {
                handleReviewMatches();
            } else if (unlinkedProfileCount === 0) {
                onScrapingComplete?.();
            }
        }
    }, [
        scrapingProfiles.size,
        unlinkedProfileCount,
        matchReviewOpen,
        handleReviewMatches,
        onScrapingComplete,
    ]);

    const getStatusChip = (profile: BaseballRecruitProfile) => {
        if (scrapingProfiles.has(profile.uuid)) {
            return (
                <Chip
                    icon={<CircularProgress size={14} />}
                    label="Scraping..."
                    size="small"
                    color="info"
                />
            );
        }
        switch (profile.scrape_status) {
            case 'active':
                return (
                    <Chip
                        icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                        label={
                            profile.last_scraped_at
                                ? `Updated ${new Date(
                                      profile.last_scraped_at,
                                  ).toLocaleDateString()}`
                                : 'Active'
                        }
                        size="small"
                        color="success"
                        variant="outlined"
                    />
                );
            case 'failed':
                return (
                    <Chip
                        icon={<ErrorIcon sx={{ fontSize: 16 }} />}
                        label="Failed"
                        size="small"
                        color="error"
                        variant="outlined"
                    />
                );
            default:
                return (
                    <Chip
                        icon={<ScheduleIcon sx={{ fontSize: 16 }} />}
                        label="Pending"
                        size="small"
                        variant="outlined"
                    />
                );
        }
    };

    const getPlayerDisplayName = (profile: BaseballRecruitProfile) => {
        if (profile.individual?.name) return profile.individual.name;
        if (profile.player_bio) {
            return `${profile.player_bio.first_name} ${profile.player_bio.last_name}`;
        }
        try {
            const url = new URL(profile.maxpreps_url);
            const parts = url.pathname.split('/').filter(Boolean);
            const nameSlug = parts.find(
                (p) =>
                    p !== 'athlete' && p !== 'baseball' && p !== 'stats' && !p.includes('career'),
            );
            if (nameSlug) return nameSlug.replace(/-/g, ' ');
        } catch {
            // ignore URL parse errors
        }
        return 'Unknown Player';
    };

    const normalizeMaxprepsUrl = (url: string): string => {
        try {
            const parsed = new URL(url);
            if (!parsed.hostname.includes('maxpreps.com')) return url;

            const parts = parsed.pathname.split('/').filter(Boolean);
            const athletesIdx = parts.indexOf('athletes');
            if (athletesIdx === -1 || athletesIdx + 1 >= parts.length) return url;

            const baseballIdx = parts.indexOf('baseball', athletesIdx);

            if (baseballIdx !== -1) {
                const newParts = parts.slice(0, baseballIdx + 1);
                newParts.push('stats');
                parsed.pathname = `/${newParts.join('/')}/`;
            } else {
                const newParts = parts.slice(0, athletesIdx + 2);
                newParts.push('baseball', 'stats');
                parsed.pathname = `/${newParts.join('/')}/`;
            }

            return parsed.toString();
        } catch {
            return url;
        }
    };

    const isValidMaxprepsUrl = (url: string) => {
        try {
            return url.includes('maxpreps.com');
        } catch {
            return false;
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    background: alpha(theme.palette.background.paper, 0.95),
                    backdropFilter: 'blur(10px)',
                    borderRadius: 2,
                },
            }}
        >
            <DialogTitle
                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
                <Typography variant="h6">Roster and Stat Scrapers</Typography>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                {successMessage && (
                    <Alert severity="success" onClose={() => setSuccessMessage('')} sx={{ mb: 2 }}>
                        {successMessage}
                    </Alert>
                )}
                {errorMessage && (
                    <Alert severity="error" onClose={() => setErrorMessage('')} sx={{ mb: 2 }}>
                        {errorMessage}
                    </Alert>
                )}

                {isActive && (
                    <Fade in>
                        <Box
                            sx={{
                                mb: 3,
                                p: 3,
                                borderRadius: 2,
                                background: `linear-gradient(135deg, ${alpha(
                                    theme.palette.primary.main,
                                    0.05,
                                )} 0%, ${alpha(theme.palette.info.main, 0.08)} 100%)`,
                                border: '1px solid',
                                borderColor: alpha(theme.palette.primary.main, 0.15),
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                    mb: 2,
                                }}
                            >
                                <BaseballIcon
                                    sx={{
                                        fontSize: 36,
                                        color: 'primary.main',
                                        animation: 'spin 2s linear infinite',
                                        '@keyframes spin': {
                                            '0%': { transform: 'rotate(0deg)' },
                                            '100%': { transform: 'rotate(360deg)' },
                                        },
                                    }}
                                />
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="subtitle1" fontWeight={700}>
                                        {searchingBr
                                            ? 'Searching Baseball Reference...'
                                            : importingCollegeStats
                                            ? 'Importing College Stats...'
                                            : refreshingCollegeStats
                                            ? 'Refreshing College Stats...'
                                            : importingCollegeRoster
                                            ? 'Importing College Roster...'
                                            : importingPg
                                            ? 'Importing from Perfect Game...'
                                            : importingRoster
                                            ? 'Importing Team Roster...'
                                            : 'Scraping Player Stats...'}
                                    </Typography>
                                    <Fade in key={statusMessageIdx}>
                                        <Typography variant="body2" color="text.secondary">
                                            {activeMessages[statusMessageIdx]}
                                        </Typography>
                                    </Fade>
                                    {(importingRoster ||
                                        importingPg ||
                                        importingCollegeRoster ||
                                        importingCollegeStats ||
                                        refreshingCollegeStats ||
                                        searchingBr) && (
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ mt: 0.5, display: 'block' }}
                                        >
                                            {searchingBr
                                                ? 'This typically takes 1-3 minutes depending on roster size.'
                                                : importingCollegeStats || refreshingCollegeStats
                                                ? 'This typically takes 30-90 seconds depending on roster size.'
                                                : importingCollegeRoster
                                                ? 'This typically takes 10-30 seconds.'
                                                : importingPg
                                                ? 'This typically takes 30-60 seconds depending on the number of commitments.'
                                                : 'This typically takes 1-2 minutes depending on roster size.'}
                                        </Typography>
                                    )}
                                    {!importingRoster &&
                                        !importingPg &&
                                        !importingCollegeRoster &&
                                        scrapingTotal > 0 && (
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ mt: 0.5, display: 'block' }}
                                            >
                                                Scraping full stats can take up to 3 minutes. Please
                                                don&apos;t close this window.
                                            </Typography>
                                        )}
                                </Box>
                                {searchingBr && (
                                    <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                                        <Typography
                                            variant="body2"
                                            fontWeight={700}
                                            fontFamily="monospace"
                                            sx={{ color: '#00897b' }}
                                        >
                                            {formatElapsed(brElapsedSeconds)}
                                        </Typography>
                                    </Box>
                                )}
                                {!importingRoster && !searchingBr && scrapingTotal > 0 && (
                                    <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                                        <Chip
                                            label={`${
                                                scrapingTotal - scrapingProfiles.size
                                            } / ${scrapingTotal}`}
                                            color="primary"
                                            variant="outlined"
                                            sx={{ fontWeight: 700, fontSize: '0.9rem' }}
                                        />
                                        <Typography
                                            variant="body2"
                                            fontWeight={700}
                                            fontFamily="monospace"
                                            sx={{ mt: 0.5, color: 'primary.main' }}
                                        >
                                            {formatElapsed(elapsedSeconds)}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ display: 'block' }}
                                        >
                                            {getEta()}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>

                            {importingRoster ||
                            importingPg ||
                            importingCollegeRoster ||
                            importingCollegeStats ||
                            refreshingCollegeStats ? (
                                <LinearProgress sx={{ borderRadius: 1, height: 6 }} />
                            ) : (
                                <LinearProgress
                                    variant="determinate"
                                    value={scrapeProgress}
                                    sx={{ borderRadius: 1, height: 6 }}
                                />
                            )}

                            {completedPlayers.length > 0 && (
                                <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {completedPlayers.slice(-8).map((name, i) => (
                                        <Fade in key={`${name}-${i}`}>
                                            <Chip
                                                label={name}
                                                size="small"
                                                color="success"
                                                variant="outlined"
                                                sx={{ fontSize: '0.75rem' }}
                                            />
                                        </Fade>
                                    ))}
                                </Box>
                            )}
                        </Box>
                    </Fade>
                )}

                {unlinkedProfileCount > 0 && scrapingProfiles.size === 0 && (
                    <Alert
                        severity="warning"
                        sx={{ mb: 2 }}
                        action={
                            <Button
                                color="inherit"
                                size="small"
                                onClick={handleReviewMatches}
                                disabled={loadingMatches}
                            >
                                {loadingMatches ? 'Loading...' : 'Review Now'}
                            </Button>
                        }
                    >
                        <strong>{unlinkedProfileCount} scraped player(s)</strong> are not linked to
                        a member. Review matches to link or create individuals.
                    </Alert>
                )}

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                        gap: 1,
                        alignItems: 'start',
                        mb: 1,
                    }}
                >
                    <GetPlayerStatsSection
                        scrapingAll={scrapingAll}
                        scrapingProfiles={scrapingProfiles}
                        scrapingTotal={scrapingTotal}
                        profiles={profiles}
                        searchingMembers={searchingMembers}
                        searchingBr={searchingBr}
                        isActive={isActive}
                        onScrapeAll={handleScrapeAll}
                        onSearchMembers={handleSearchMembers}
                        onSearchBr={handleSearchBr}
                    />

                    <ImportRostersSection
                        onPgAccordionChange={handlePgAccordionExpand}
                        pgColleges={pgColleges}
                        pgCollegesLoading={pgCollegesLoading}
                        pgCollege={pgCollege}
                        onPgCollegeChange={(v) => setPgCollege(v)}
                        pgYear={pgYear}
                        onPgYearChange={(y) => setPgYear(y)}
                        importingPg={importingPg}
                        onImportPgCommitments={handleImportPgCommitments}
                        pgUnmatched={pgUnmatched}
                        onClearPgUnmatched={() => setPgUnmatched([])}
                        collegeLogoUrl={collegeLogoUrl}
                        onClearCollegeLogo={() => setCollegeLogoUrl('')}
                        settingLogo={settingLogo}
                        onSetGroupLogo={handleSetGroupLogo}
                        collegeRosterUrl={collegeRosterUrl}
                        onCollegeRosterUrlChange={(url) => setCollegeRosterUrl(url)}
                        importingCollegeRoster={importingCollegeRoster}
                        onImportCollegeRoster={handleImportCollegeRoster}
                        rosterUrl={rosterUrl}
                        onRosterUrlChange={(url) => setRosterUrl(url)}
                        importingRoster={importingRoster}
                        onImportRoster={handleImportRoster}
                        isValidMaxprepsUrl={isValidMaxprepsUrl}
                    />

                    <ImportStatsSection
                        newUrl={newUrl}
                        onNewUrlChange={(url) => setNewUrl(url)}
                        adding={adding}
                        onAddUrl={handleAddUrl}
                        isValidMaxprepsUrl={isValidMaxprepsUrl}
                        collegeStatsUrl={collegeStatsUrl}
                        onCollegeStatsUrlChange={(url) => setCollegeStatsUrl(url)}
                        importingCollegeStats={importingCollegeStats}
                        onImportCollegeStats={handleImportCollegeStats}
                        refreshingCollegeStats={refreshingCollegeStats}
                        isActive={isActive}
                        onRefreshCollegeStats={handleRefreshCollegeStats}
                        profilesWithCollegeStats={profilesWithCollegeStats}
                        latestCollegeScrape={latestCollegeScrape}
                        collegeStatsUnmatched={collegeStatsUnmatched}
                        onOpenCollegeStatsMatch={() => setCollegeStatsMatchOpen(true)}
                        summerStatsUrl={summerStatsUrl}
                        onSummerStatsUrlChange={(url) => setSummerStatsUrl(url)}
                        importingSummerStats={importingSummerStats}
                        onImportSummerStats={handleImportSummerStats}
                        summerStatsUnmatched={summerStatsUnmatched}
                        onOpenSummerStatsMatch={() => setSummerStatsMatchOpen(true)}
                    />

                    <ProfilePhotosSection
                        detectedRosterUrl={detectedRosterUrl}
                        photoRefreshUrl={photoRefreshUrl}
                        onPhotoRefreshUrlChange={(url) => setPhotoRefreshUrl(url)}
                        refreshingPhotos={refreshingPhotos}
                        onRefreshCollegePhotos={handleRefreshCollegePhotos}
                    />
                </Box>

                <PreviousScrapesSection
                    profiles={profiles}
                    scrapingProfiles={scrapingProfiles}
                    getPlayerDisplayName={getPlayerDisplayName}
                    getStatusChip={getStatusChip}
                    onScrape={handleScrape}
                    onDeleteProfile={handleDeleteProfile}
                    onUnlinkProfile={handleUnlinkProfile}
                />
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} color="inherit">
                    Close
                </Button>
                {unlinkedProfileCount > 0 && (
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={handleReviewMatches}
                        disabled={loadingMatches}
                        startIcon={
                            loadingMatches ? <CircularProgress size={18} /> : <PeopleAltIcon />
                        }
                    >
                        Review Matches ({unlinkedProfileCount})
                    </Button>
                )}
            </DialogActions>

            {matchResults && group && (
                <BaseballRecruitMatchReview
                    open={matchReviewOpen}
                    onClose={() => setMatchReviewOpen(false)}
                    results={matchResults}
                    group={group}
                    onConfirmed={() => {
                        onSuccess?.();
                        setMatchResults(null);
                        onScrapingComplete?.();
                    }}
                />
            )}

            {group && (
                <CollegeRosterReview
                    open={collegeRosterReviewOpen}
                    onClose={() => setCollegeRosterReviewOpen(false)}
                    matched={collegeRosterMatched}
                    unmatched={collegeRosterUnmatched}
                    groupUuid={group.uuid}
                    onConfirmed={() => {
                        setCollegeRosterReviewOpen(false);
                        setCollegeRosterMatched([]);
                        setCollegeRosterUnmatched([]);
                        onSuccess?.();
                        handleSearchMembers();
                    }}
                />
            )}

            {group && collegeStatsUnmatched.length > 0 && (
                <CollegeStatsMatchReview
                    open={collegeStatsMatchOpen}
                    onClose={() => setCollegeStatsMatchOpen(false)}
                    unmatchedPlayers={collegeStatsUnmatched}
                    profiles={profiles}
                    groupUuid={group.uuid}
                    statsUrl={collegeStatsMatchUrl}
                    onConfirmed={() => {
                        setCollegeStatsUnmatched([]);
                        setCollegeStatsMatchOpen(false);
                        onSuccess?.();
                    }}
                />
            )}
            {group?.uuid && summerStatsUnmatched.length > 0 && (
                <SummerStatsMatchReview
                    open={summerStatsMatchOpen}
                    onClose={() => setSummerStatsMatchOpen(false)}
                    unmatchedPlayers={summerStatsUnmatched}
                    profiles={profiles}
                    groupUuid={group.uuid}
                    brUrl={summerStatsMatchUrl}
                    onConfirmed={() => {
                        setSummerStatsUnmatched([]);
                        setSummerStatsMatchOpen(false);
                        onSuccess?.();
                    }}
                />
            )}
        </Dialog>
    );
};

export default BaseballRecruitScraperDialog;
